import {
  AfterContentInit,
  ContentChild,
  ContentChildren,
  Directive,
  ElementRef,
  HostListener,
  OnDestroy,
  QueryList,
  Renderer2,
} from '@angular/core';
import { debounce } from 'lodash';
import { Subject } from 'rxjs';
import { WindowRefService } from 'i2g-ng-utils';
import { startWith, takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[horizontalNav]',
})
export class HorizontalNavDirective implements AfterContentInit, OnDestroy {
  @ContentChild('prevBtn', { static: false }) prevBtn: ElementRef;
  @ContentChild('nextBtn', { static: false }) nextBtn: ElementRef;
  @ContentChild('horizontalNavList', { static: false }) navList: ElementRef;

  @ContentChildren('horizontalNavItem', { descendants: true })
  navItems: QueryList<ElementRef>;

  @HostListener('window:resize')
  onWindowResize() {
    this.recalculateArrowVisibility();
  }

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _atEnd: boolean;
  private _window: Window;
  private _el: any;

  constructor(
    private _renderer: Renderer2,
    private _elementRef: ElementRef,
    windowRefService: WindowRefService
  ) {
    this._window = windowRefService.nativeWindow;
    this._el = this._elementRef.nativeElement;
  }

  ngAfterContentInit() {
    if (this.navList && this.navItems && this.prevBtn && this.nextBtn) {
      this.hideArrowBtn(this.prevBtn);
      this.hideArrowBtn(this.nextBtn);

      this._renderer.listen(
        this.prevBtn.nativeElement,
        'click',
        (evt: MouseEvent) => {
          if (this._atEnd) {
            this._atEnd = false;

            this._renderer.setStyle(
              this.navList.nativeElement,
              'transform',
              `translateX(0)`
            );

            this.hideArrowBtn(this.prevBtn);
            this.showArrowBtn(this.nextBtn);
          }
        }
      );

      this._renderer.listen(
        this.nextBtn.nativeElement,
        'click',
        (evt: MouseEvent) => {
          if (!this._atEnd) {
            this._atEnd = true;

            this._renderer.setStyle(
              this.navList.nativeElement,
              'transform',
              `translateX(-${this.recalculateOverflow()}px)`
            );

            this.showArrowBtn(this.prevBtn);
            this.hideArrowBtn(this.nextBtn);
          }
        }
      );

      this.navItems.changes
        .pipe(takeUntil(this._destroyed), startWith(this.navItems))
        .subscribe((items) => this.recalculateArrowVisibility());
    }
  }

  recalculateOverflow(): number {
    if (this.navItems && this.navList) {
      const navItemsTotalWidth = this.navItems.reduce(
        (totalWidth: number, navItemEl: ElementRef) => {
          const elWidth = navItemEl.nativeElement.offsetWidth;

          return totalWidth + elWidth;
        },
        0
      );

      const navListWidth = this.navList.nativeElement.clientWidth;

      return navItemsTotalWidth - navListWidth;
    }

    return 0;
  }

  recalculateArrowVisibility = debounce(() => {
    const widthOverflow = this.recalculateOverflow();

    if (widthOverflow > 0) {
      if (this._atEnd) {
        this.showArrowBtn(this.prevBtn);
      } else {
        this.showArrowBtn(this.nextBtn);
      }
    } else {
      this._atEnd = false;

      this.hideArrowBtn(this.prevBtn);
      this.hideArrowBtn(this.nextBtn);
    }
  }, 500);

  hideArrowBtn(btn: ElementRef) {
    this._renderer.setAttribute(btn.nativeElement, 'hidden', '');
  }

  showArrowBtn(btn: ElementRef) {
    this._renderer.removeAttribute(btn.nativeElement, 'hidden');
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
